import type { AppDefinition } from "../core/Core.api";
import {
    blend30App,
    blendApp,
    craftApp,
    inpainting40App,
    inpaintingApp,
    magicFit25App,
    magicFit30App,
    magicFit40App,
    variations20App,
    variations25App,
    variations30App,
    variations40App,
} from "./AppDefinitions";

/*
 * The SpliceGen capabilities, and every model version of each.
 *
 * Home used to carry one section per model release, which meant the same seven
 * names repeated down the page with nothing to tell them apart but a version
 * badge — the versions of a capability are the same tool, differing in
 * inference parameters rather than in what they do. This groups them the other
 * way round: one entry per capability, its releases listed underneath.
 *
 * Older releases stay deployed and reachable by URL, and All Capabilities
 * still lists every model under its version heading. This changes what the
 * home page offers, not what exists.
 *
 * Not every SpliceGen-backed tool belongs here. Fill + Fit and Modulate are
 * Shahan's, and sit with the other authored apps: this collection is the core
 * capabilities, not everything built on the models.
 */

export interface SpliceGenCapability {
    readonly title: string;
    /**
     * What the capability does, for its home card — the tool, not the model.
     *
     * Separate from `AppDefinition.description`, which describes one release
     * and says which model it runs on. A card resolves to a different release
     * depending on who is looking, so a per-release sentence there would tell
     * the R&D team about 4.0 and everyone else about 3.0 on what is meant to
     * be the same card. The release's own sentence still appears, in the app's
     * details panel, where it is true of exactly one thing.
     */
    readonly description: string;
    /**
     * Newest release first. The order here is authoritative rather than
     * derived from `AppDefinition.version`: `v3.5` and `v3.0` are both in use
     * and do not sort against each other in any way worth relying on.
     */
    readonly versions: readonly AppDefinition[];
}

export const kSpliceGenCapabilities: readonly SpliceGenCapability[] = [
    {
        title: "Variations",
        description: "Create new takes that follow your key/scale guidance",
        versions: [variations40App, variations30App, variations25App, variations20App],
    },
    {
        title: "Fit",
        description: "Adapt loops to fit your own sound",
        versions: [magicFit40App, magicFit30App, magicFit25App],
    },
    {
        title: "Blend",
        description: "Combine loops with experimental sound design controls",
        versions: [blend30App, blendApp],
    },
    {
        title: "Craft",
        description: "Turn a loop into a playable instrument",
        versions: [craftApp],
    },
    {
        title: "Fill",
        description: "Fill part of a loop with a new idea",
        versions: [inpainting40App, inpaintingApp],
    },
];

/**
 * The newest release of a capability this viewer can actually open, or
 * `undefined` when they can open none of them.
 *
 * Which release that is depends on who is looking: the 4.0 apps are R&D-only
 * while 3.0 is out to internal, Soundcheck and partners, so the same card resolves to
 * 4.0 for the R&D team and 3.0 for everyone else rather than disappearing.
 *
 * Takes a predicate instead of reading audience state directly, so this file
 * stays data and the caller keeps deciding what "can open" means.
 */
export const getLatestAvailableVersion = (
    capability: SpliceGenCapability,
    canOpen: (app: AppDefinition) => boolean,
): AppDefinition | undefined => capability.versions.find(canOpen);
